import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { verifyEmail } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { Button, Loading } from '../components/common';

const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { updateUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setError('No verification token provided');
        return;
      }

      try {
        const result = await verifyEmail(token);

        if (result.success) {
          setStatus('success');
          // Update user state if logged in
          if (result.user) {
            updateUser({ isEmailVerified: true });
          }
        } else {
          setStatus('error');
          setError(result.error);
        }
      } catch (err) {
        setStatus('error');
        setError('An unexpected error occurred');
      }
    };

    verify();
  }, [token, updateUser]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
        <div className="max-w-md w-full text-center">
          <Loading size="lg" text="Verifying your email..." />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">K</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">Kwikly</span>
            </Link>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg
              className="w-12 h-12 text-red-500 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Verification Failed
            </h3>
            <p className="text-red-600 mb-4">
              {error || 'This verification link is invalid or has expired.'}
            </p>
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/dashboard')}
                fullWidth
              >
                Go to Dashboard
              </Button>
            ) : (
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Go to Login
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">K</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">Kwikly</span>
          </Link>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-green-500 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Email Verified!
          </h3>
          <p className="text-gray-600 mb-4">
            Your email has been successfully verified. You now have full access to all features.
          </p>
          <Button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            fullWidth
          >
            {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
